/**
 * Mirrors `api/src/modules/identity/domain/password.ts`'s `PASSWORD_POLICY`/`checkPassword`
 * — client-SIDE only, for the live-updating checklist SCREEN_SPECS.md I3 calls for ("policy
 * rules stated up front, not discovered only on failure"). This is fast feedback, never the
 * authority: the server re-checks the exact same rules on submit (`POST
 * /auth/password/change`), and its `VALIDATION_FAILED` `fieldErrors` codes on `newPassword`
 * — `too_short` | `too_long` | `blocked_term` | `contains_username` — are what
 * `AccountSettingsPage` actually renders as the authoritative outcome. A password this
 * checklist marks "OK" can still be refused by the server (e.g. a blocked-term list the
 * server updates without a client deploy); the reverse should not happen, since both read
 * the same rule set today.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** Same list as the server's `BLOCKED_SUBSTRINGS` — not a secret, so duplicating it here for instant feedback costs nothing. */
const BLOCKED_SUBSTRINGS = ['password', 'nepal', 'ministry', 'mantralaya', '123456', 'qwerty', 'admin'];

export type PasswordRejectionCode = 'too_short' | 'too_long' | 'blocked_term' | 'contains_username';

export interface PasswordCheck {
  acceptable: boolean;
  rejections: readonly PasswordRejectionCode[];
}

export function checkPasswordClientSide(candidate: string, username: string | undefined): PasswordCheck {
  const rejections: PasswordRejectionCode[] = [];
  if (candidate.length < PASSWORD_MIN_LENGTH) rejections.push('too_short');
  if (candidate.length > PASSWORD_MAX_LENGTH) rejections.push('too_long');

  const lowered = candidate.toLowerCase();
  if (BLOCKED_SUBSTRINGS.some((term) => lowered.includes(term))) rejections.push('blocked_term');

  const loweredUsername = username?.toLowerCase();
  if (loweredUsername !== undefined && loweredUsername.length >= 3 && lowered.includes(loweredUsername)) {
    rejections.push('contains_username');
  }

  return { acceptable: rejections.length === 0, rejections };
}
